import { Outlet, NavLink } from 'react-router-dom';
import { Header } from '@/widgets/header/Header';
import { Sidebar } from '@/widgets/sidebar/Sidebar';
import { EmailVerifyBanner } from '@/widgets/email-verify-banner/EmailVerifyBanner';
import { AppIcon, type IconName } from '@/shared/ui';
import { useLmsMe, hasAnyRole, type LmsRole } from '@/shared/lib/lms/roles';

// Каталог/Мои курсы/Уведомления доступны любому авторизованному (student дефолт).
// Остальные разделы требуют конкретных LMS-ролей; admin_lms видит всё.
type Tab = { to: string; label: string; icon: IconName; roles?: LmsRole[] };

const LMS_TABS: readonly Tab[] = [
  { to: '/lms/home', label: 'Кабинет', icon: 'home' },
  { to: '/lms/my', label: 'Мои курсы', icon: 'courses' },
  { to: '/lms/notifications', label: 'Уведомления', icon: 'bell' },
  { to: '/lms/teach', label: 'Преподавание', icon: 'teach', roles: ['teacher', 'methodist'] },
  { to: '/lms/curate', label: 'Менеджер группы', icon: 'groups', roles: ['curator'] },
  { to: '/lms/analytics', label: 'Аналитика', icon: 'analytics', roles: ['methodist'] },
  { to: '/lms/admin', label: 'Админ-панель', icon: 'admin', roles: ['methodist', 'admin_lms'] },
  { to: '/lms/admin/courses', label: 'Курсы', icon: 'courses', roles: ['methodist', 'admin_lms'] },
  { to: '/lms/admin/programs', label: 'Программы', icon: 'programs', roles: ['methodist', 'admin_lms'] },
  { to: '/lms/admin/tariffs', label: 'Тарифы', icon: 'billing', roles: ['methodist', 'admin_lms'] },
  { to: '/lms/admin/orders', label: 'Заказы', icon: 'orders', roles: ['admin_lms'] },
  { to: '/lms/admin/payments', label: 'Платежи', icon: 'payments', roles: ['admin_lms'] },
  { to: '/lms/admin/promo', label: 'Промо', icon: 'promo', roles: ['admin_lms'] },
  { to: '/lms/admin/schedule', label: 'Расписание', icon: 'calendar', roles: ['methodist', 'admin_lms'] },
  { to: '/lms/admin/vacancies', label: 'Вакансии', icon: 'briefcase', roles: ['methodist', 'admin_lms'] },
  { to: '/lms/admin/certificates', label: 'Сертификаты', icon: 'certificate', roles: ['admin_lms'] },
  { to: '/lms/admin/audit', label: 'Аудит', icon: 'audit', roles: ['admin_lms'] },
  { to: '/lms/admin/users', label: 'Пользователи', icon: 'users', roles: ['admin_lms'] },
  { to: '/lms/admin/enrollments', label: 'Зачисления', icon: 'enroll', roles: ['admin_lms'] },
];

export function LmsLayout() {
  const { data: me, isLoading } = useLmsMe();
  const roles = me?.roles;

  const visible = LMS_TABS.filter((t) => {
    if (!t.roles) return true;
    return hasAnyRole(roles, ...t.roles);
  });

  return (
    <div className="flex h-screen overflow-hidden bg-surface-secondary">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <EmailVerifyBanner />
        <div className="border-b border-surface-tertiary bg-surface px-6 pt-2">
          <nav className="flex gap-1 overflow-x-auto">
            {isLoading && !roles ? (
              <div className="h-9 w-full animate-pulse rounded-t-md bg-surface-secondary" />
            ) : (
              visible.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.to === '/lms/catalog' || t.to === '/lms/admin'}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 whitespace-nowrap rounded-t-md px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'border-b-2 border-primary-600 font-medium text-primary-700'
                        : 'text-text-secondary hover:text-text-primary'
                    }`
                  }
                >
                  <AppIcon name={t.icon} size="sm" />
                  {t.label}
                </NavLink>
              ))
            )}
          </nav>
        </div>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
